import {
  Engagement,
  EngagementApiService,
  EngagementMode,
  EngagementStatus,
  EngagementStep,
  EngagementStepStatus,
  EngagementStepType,
} from '@aiwyn/api';
import { AssetUriService, ConfigService, LaunchDarklyFlag, LaunchDarklyService, LaunchDarklyState } from '@aiwyn/config';
import {
  BaseContact,
  getFirstOfQueryList,
  getInitials,
  hideHubspot,
  ignoreFalsy,
  ignoreNullish,
  isLoaded,
  LoadableStatus,
  SharedModule,
  SnackbarService,
  SnackbarType,
} from '@aiwyn/shared';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, NgClass, NgSwitch, NgSwitchCase, NgSwitchDefault, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, defer, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ROUTE_TOKEN_ENGAGEMENTS_BASE, ROUTE_TOKEN_MAIN_BASE } from '../../../../routing-constants';
import { AppState } from '../../../../state/app.state';
import {
  ConsentStepComponent,
  EngagementCompleteDialogComponent,
  EngagementContactSelectComponent,
  EngagementContactSelectOption,
  EngagementErrorDialogComponent,
  EngagementLetterStepComponent,
  PaymentInfoStepComponent,
} from '../../components';
import { derivePrepaymentStepState } from '../../components/payment-info-step/prepayment-step-state';
import { resolveEngagementFooterButtonVisibility } from './engagement-footer-buttons';
import {
  DestinationEngagementStepRef,
  DestinationEngagementStepType,
  ENGAGEMENT_STEP_FAILED_MESSAGE,
  EngagementDocumentUpdatedStrategyModel,
  EngagementStepComponent,
  EngagementStepLabel,
  EngagementStepSubmissionResult,
  FooterButtonType,
} from '../../models';
import {
  EngagementsState,
  GetEngagementHub,
  MoveToEngagementStep,
  RemoveEngagementStepPendingChanges,
  SetActiveSigningContact,
  SetEngagementCurrentStepRef,
} from '../../state';

interface EngagementStepTemplate {
  ref: TemplateRef<EngagementStepComponent>,
  context: EngagementStep
}

const ENGAGEMENT_STEP_COMPONENT_ID = 'engagementStepComponent';
const GENERIC_ENGAGEMENT_STEP_SUBMISSION_FAILURE_MESSAGE = 'There was an error submitting the step.';

@Component({
  selector: 'app-view-engagement-container',
  templateUrl: './view-engagement-container.component.html',
  styleUrls: ['./view-engagement-container.component.scss'],
  standalone: true,
  imports: [
    RouterModule,
    MatDrawer,
    MatDrawerContent,
    MatDrawerContainer,
    SharedModule,
    AsyncPipe,
    NgSwitch,
    NgClass,
    MatProgressSpinner,
    NgSwitchCase,
    NgSwitchDefault,
    NgTemplateOutlet,
    EngagementLetterStepComponent,
    ConsentStepComponent,
    PaymentInfoStepComponent,
    EngagementContactSelectComponent,
],
})
export class ViewEngagementContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly engagementApiService = inject(EngagementApiService);

  readonly isUsingSecureLink = input<boolean>();
  readonly onNavigateToNextEntity = input<() => void>();
  readonly onNavigateToPreviousEntity = input<() => void>();
  readonly showSideDrawer = input<() => boolean>();

  readonly sideDrawerOpened = output<boolean>();

  readonly LoadableStatus = LoadableStatus;
  readonly EngagementStatus = EngagementStatus;
  readonly EngagementStepStatus = EngagementStepStatus;
  readonly EngagementMode = EngagementMode;
  readonly EngagementStepLabel = EngagementStepLabel;
  readonly ENGAGEMENT_STEP_FAILED_MESSAGE = ENGAGEMENT_STEP_FAILED_MESSAGE;

  private readonly _unsubscribe$ = new Subject<void>();
  private readonly _hasViewedEngagement$ = new BehaviorSubject<boolean>(false);
  private _documentUpdateDialogOpen = false;
  private _ignoreDocumentUpdates = false;
  private _hubspotCleanup: (() => void) | null = null;

  // Engagement Step Templates
  @ViewChild(`${EngagementStepType.LETTER}`, { read: TemplateRef }) engagementLetterStep: TemplateRef<EngagementLetterStepComponent>;
  @ViewChild(`${EngagementStepType.CONSENT}`, { read: TemplateRef }) consentStep: TemplateRef<ConsentStepComponent>;
  @ViewChild(`${EngagementStepType.PAYMENT}`, { read: TemplateRef }) paymentInfoStep: TemplateRef<PaymentInfoStepComponent>;

  // Current Engagement Step Component
  @ViewChildren(ENGAGEMENT_STEP_COMPONENT_ID) engagementStepComponents: QueryList<EngagementStepComponent>;

  // Engagement Wizard Footer Buttons
  @ViewChild(`${FooterButtonType.PREVIOUS_STEP}`, { read: TemplateRef }) previousStepFooterButton: TemplateRef<HTMLElement>;
  @ViewChild(`${FooterButtonType.NEXT_STEP}`, { read: TemplateRef }) nextStepFooterButton: TemplateRef<HTMLElement>;
  @ViewChild(`${FooterButtonType.APPROVE_AND_SUBMIT}`, { read: TemplateRef }) approveAndSubmitFooterButton: TemplateRef<HTMLElement>;
  @ViewChild(`${FooterButtonType.COMPLETE_ENGAGEMENT}`, { read: TemplateRef }) completeEngagementFooterButton: TemplateRef<HTMLElement>;
  @ViewChild(`${FooterButtonType.CLOSE_ENGAGEMENT}`, { read: TemplateRef }) closeEngagementFooterButton: TemplateRef<HTMLElement>;
  @ViewChild(`${FooterButtonType.UPDATE_PAYMENT_METHOD}`, { read: TemplateRef }) updatePaymentMethodFooterButton: TemplateRef<HTMLElement>;
  @ViewChild(`${FooterButtonType.PREVIOUS_FIELD}`, { read: TemplateRef }) previousFieldFooterButton: TemplateRef<HTMLElement>;
  @ViewChild(`${FooterButtonType.NEXT_FIELD}`, { read: TemplateRef }) nextFieldFooterButton: TemplateRef<HTMLElement>;

  readonly toggleSideDrawer$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  isMobile$: Observable<boolean> = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(
      map(breakpointMatched => breakpointMatched.matches),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  showSideDrawer$: Observable<boolean> = combineLatest([
    this.toggleSideDrawer$,
    this.isMobile$
  ]).pipe(
    // If at a mobile breakpoint, drawer is controlled by the toggle, otherwise it is always open
    map(([isMobileOpen, isMobile]) => !isMobile || isMobileOpen),
    takeUntil(this._unsubscribe$),
  );

  sideDrawerMode$: Observable<'over' | 'side'> = this.isMobile$.pipe(
    map(isMobile => isMobile ? 'over' : 'side'),
  );

  currentStepIndex$: Observable<number> = this.store.select(EngagementsState.getSelectedEngagementVisibleStepIndex);
  totalSteps$: Observable<number> = this.store.select(EngagementsState.getSelectedEngagementVisibleStepCount);

  completeEngagementSuccessResponse$ = defer(() => {
    this.setProcessingSubmission(false);

    // PP-3635: a synchronous prepayment failure surfaces as a "successful" submission — the mutation error
    // is swallowed by RecoverFromEngagementCompletionFailure, which refreshes the hub so prepaymentStatus
    // becomes FAILED. In that case render the dialog's error state instead of "Thank you!", and on close do
    // NOT route out to the engagements table — leave the client on the failed payment step so they can retry
    // in place (its footer CTA is already "Try Again").
    const prepaymentFailed = this.isFailedPrepaymentRetry();

    return this.dialog.open(EngagementCompleteDialogComponent, { data: { prepaymentFailed } }).afterClosed().pipe(
      switchMap(_ => {
        if (!prepaymentFailed) {
          return this.router.navigateByUrl(`${ROUTE_TOKEN_MAIN_BASE}/${ROUTE_TOKEN_ENGAGEMENTS_BASE}`, { state: { forceRefresh: true } });
        }

        // PP-3794: submitStep raised isStepLoading$ expecting a step transition to lower it again once the
        // next step's footer pipeline ran. Staying put means that transition never comes and nothing else
        // ever lowers it, so the footer stays disabled and "Try Again" is unclickable short of a reload.
        this.isStepLoading$.next(false);
        return of(undefined);
      }),
    );
  });

  submitStepSuccessResponse$ = defer(() => this.store.dispatch(new MoveToEngagementStep('next')));

  submitLastStepWithIncompletePriorStepsSuccessResponse$ = defer(() => {
    /**
     * Refresh details of current step.
     */
    const currentStep = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStep);

    this.currentStepTemplateSubject$.next({
      ref: this.marshallCurrentStepTemplateRef(currentStep.type),
      context: currentStep,
    });

    return of(null);
  });

  engagementLoadedSuccessfully$ = new ReplaySubject<boolean>(1);
  viewEngagement$ = this.store.select(EngagementsState.getSelectedEngagementReportStatus).pipe(
    isLoaded(true),
    tap(engagementReportStatus => this.engagementLoadedSuccessfully$.next(engagementReportStatus === LoadableStatus.LOADED)),
    take(1),
    switchMap(_ => {
      if (!this.isUsingSecureLink() && this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStepRef) == null) {
        return this.store.dispatch(new MoveToEngagementStep(undefined));
      }
      return of(undefined);
    }),
    takeUntil(this._unsubscribe$),
  );

  selectedEngagement$: Observable<Engagement> = this.store.select(EngagementsState.getSelectedEngagementWithDate).pipe(
    filter<Engagement>(Boolean),
    tap(({ name }) => document.title = name),
  );

  isEngagementReadonly$ = this.store.select(EngagementsState.isEngagementReadonly);
  isEngagementNonInteractive$ = this.store.select(EngagementsState.isEngagementNonInteractive);
  isEngagementOnFinalStep$ = this.store.select(EngagementsState.isSelectedEngagementOnLastIncompleteStep);
  someSelectedEngagementStepsInvalid$ = this.store.select(EngagementsState.someSelectedEngagementStepsInvalid).pipe(
    // Quick and dirty workaround to avoid the error message from flashing when it is true and the user tries to resubmit.
    debounceTime(100),
  );

  engagementReportStatus$ = this.store.select(EngagementsState.getSelectedEngagementReportStatus);
  visibleEngagementSteps$ = this.store.select(EngagementsState.getSelectedEngagementVisibleStepsDisplay);
  currentStepTemplateSubject$ = new ReplaySubject<EngagementStepTemplate>(1);

  showStepComponent = true;

  currentStep$: Observable<EngagementStep> = this.store.select(EngagementsState.getSelectedEngagementCurrentStep)
    .pipe(ignoreFalsy());

  getCurrentStepTemplateRef$ = this.currentStep$.pipe(
    ignoreNullish(),
    distinctUntilChanged((prev, curr) => prev.type === curr.type && prev.documentId === curr.documentId),
    debounceTime(0),
    tap(currentStep => {
      // Synchronously destroy the old step component before creating the new one.
      this.showStepComponent = false;
      this.changeDetectorRef.detectChanges();
      this.showStepComponent = true;
      this.currentStepTemplateSubject$.next({
        ref: this.marshallCurrentStepTemplateRef(currentStep.type),
        context: currentStep
      });
    }),
    takeUntil(this._unsubscribe$),
  );

  footerButtonTemplateRefs$ = this.currentStepTemplateSubject$.pipe(
    switchMap(() => getFirstOfQueryList(this.engagementStepComponents)),
    switchMap((currentEngagementStepComponent) => currentEngagementStepComponent.getFooterButtons()),
    withLatestFrom(this.currentStep$),
    map(([stepFooterButtonTypes, currentStep]) => {
      const isOnLastStep = this.store.selectSnapshot(EngagementsState.isSelectedEngagementOnLastStep);

      const {
        showPreviousStepButton,
        showApproveAndSubmitButton,
        showNextStepButton,
        showCompleteEngagementButton,
        showCloseEngagementButton,
      } = resolveEngagementFooterButtonVisibility({
        currentStepType: currentStep.type,
        isReadonlyCurrentStep: currentStep.isReadonly,
        isOnFirstStep: this.store.selectSnapshot(EngagementsState.isSelectedEngagementOnFirstStep),
        isOnLastStep,
        isOnLastIncompleteStep: this.store.selectSnapshot(EngagementsState.isSelectedEngagementOnLastIncompleteStep),
        isLastStepCompleted: isOnLastStep && currentStep.status === EngagementStepStatus.COMPLETE,
        isEngagementReadonly: this.store.selectSnapshot(EngagementsState.isEngagementReadonly),
        isUsingSecureLink: !!this.isUsingSecureLink(),
        // FIRM-10915 / PP-3635: a hard-failed sync prepayment on the PAYMENT step is retryable in place, so
        // the complete-engagement CTA ("Try Again") must render even though the read-only FAILED step would
        // otherwise suppress it.
        isFailedPrepaymentRetry: this.isFailedPrepaymentRetry(),
      });

      return []
        .concat(showPreviousStepButton ? this.previousStepFooterButton : [])
        .concat(stepFooterButtonTypes.map(stepFooterButtonType => this.marshallFooterButtonTemplateRef(stepFooterButtonType)))
        .concat(showApproveAndSubmitButton ? this.approveAndSubmitFooterButton : [])
        .concat(showNextStepButton ? this.nextStepFooterButton : [])
        .concat(showCompleteEngagementButton ? this.completeEngagementFooterButton : [])
        .concat(showCloseEngagementButton ? this.closeEngagementFooterButton : []);
    }),

    // The new step's footer buttons are ready, which (for Apryse steps) means its document has loaded —
    // the "we have new info" moment, so we can re-enable the controls.
    tap(() => this.isStepLoading$.next(false)),

    // Avoid rendered footer buttons while navigating between steps and during new step loading
    startWith([]),
  );


  originalDocumentTitle: string;
  isProcessingSubmission$ = new BehaviorSubject<boolean>(false);

  // True from the moment a step transition is triggered until the new step's footer buttons are ready
  // (i.e. the new document has loaded). Keeps footer controls disabled during the doc-load window so
  // rapid clicks can't fire a submit/navigation against the previous step's stale, still-rendered buttons.
  isStepLoading$ = new BehaviorSubject<boolean>(false);
  controlsDisabled$ = combineLatest([this.isProcessingSubmission$, this.isStepLoading$])
    .pipe(map(([isProcessingSubmission, isStepLoading]) => isProcessingSubmission || isStepLoading));

  isEngagementLetterManuallyUploaded$ = this.store.select(EngagementsState.isEngagementLetterManuallyUploaded);

  // FIRM-10915: on the payment step a failed prepayment turns the CTA into an in-place "Try Again"
  // retry (see PaymentInfoStepComponent.submit); otherwise keep the standard complete/authorize label.
  completeButtonText$ = combineLatest([
    this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_ENGAGEMENT_AUTOMATED_PAYMENT),
    this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_SYNC_ENGAGEMENT_PREPAYMENT),
    this.store.select(EngagementsState.getSelectedEngagementPrepayment),
    this.store.select(EngagementsState.getSelectedEngagement),
    this.currentStep$,
  ]).pipe(
    map(([isAutoDraftEnabled, syncPrepaymentEnabled, prepayment, engagement, currentStep]) => {
      if (
        currentStep?.type === EngagementStepType.PAYMENT
        && derivePrepaymentStepState(prepayment, syncPrepaymentEnabled, engagement) === 'failed'
      ) {
        return 'Try Again';
      }

      return `${isAutoDraftEnabled ? 'Authorize & ' : ''}Complete`;
    }),
  );

  showSideDrawerMonitor$: Observable<boolean> = this.toggleSideDrawer$.pipe(
    distinctUntilChanged(),
    tap(showSideDrawer => this.sideDrawerOpened.emit(showSideDrawer)),
    takeUntil(this._unsubscribe$),
  );

  readonly isSelectedEngagementReassignable$ = this.store.select(EngagementsState.isSelectedEngagementReassignable);

  // Multiple Identity Signing
  isMultipleIdentitySigningActive$ = this.store.select(EngagementsState.isMultipleIdentitySigningActive);
  activeSigningContactId$ = this.store.select(EngagementsState.getActiveSigningContactId);
  contactSelectOptions$ = this.store.select(EngagementsState.getSigningContactProfiles)
    .pipe(map(profiles => profiles.map(profile => this.mapProfileToContactSelectOption(profile))));

  readonly isContactSelectDropdownVisible = EngagementsState.IS_CONTACT_SELECT_DROPDOWN_VISIBLE;

  constructor(
    private readonly store: Store,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public readonly configService: ConfigService,
    public readonly assetUriService: AssetUriService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly snackbarService: SnackbarService,
    private readonly breakpointObserver: BreakpointObserver,
  ) {
    effect(() => {
      if (!this.showSideDrawer()) {
        return;
      }

      const showSideDrawer = this.showSideDrawer()();
      this.toggleSideDrawer$.next(showSideDrawer);
    });
  }

  /**
   * Checks if the current step is the same as the given step.
   * @param step The step to check.
   * @returns Whether the current step is the same as the given step.
   */
  isCurrentStep(step: EngagementStep): boolean {
    const currentStep = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStep);

    if (!currentStep || currentStep.type !== step.type) {
      return false;
    }

    const currentDocumentId = (currentStep.tasks?.find(task => task.documentId))?.documentId;
    const stepDocumentId = (step.tasks?.find(task => task.documentId))?.documentId;

    return currentDocumentId ? currentDocumentId === stepDocumentId : true;
  }

  /**
   * Returns the reference for the given step.
   * @param step The step to return the reference for.
   * @returns The reference for the given step.
   */
  stepReference(step: EngagementStep) {
    return {
      type: step.type,
      documentId: (step.tasks?.find(task => task.documentId))?.documentId,
    };
  }


  /**
   * FIRM-10915 / PP-3635: whether the current step is a hard-failed sync prepayment that can be retried in
   * place. Mirrors the `'failed'` branch of {@link derivePrepaymentStepState} read off live snapshots. Used
   * to (1) force the complete-engagement "Try Again" CTA to render despite the read-only FAILED step, and
   * (2) make {@link onSubmitStep} run the step submission (which fires the retry) instead of navigating away.
   * The engagement-level `prepaymentStatus` is hub-sourced and present before the step renders, so this is
   * reliable even before the prepayment read-model finishes loading.
   */
  private isFailedPrepaymentRetry(): boolean {
    const currentStep = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStep);
    if (currentStep?.type !== EngagementStepType.PAYMENT) {
      return false;
    }

    const syncPrepaymentEnabled = this.store.selectSnapshot(
      LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_SYNC_ENGAGEMENT_PREPAYMENT),
    );
    const prepayment = this.store.selectSnapshot(EngagementsState.getSelectedEngagementPrepayment);
    const engagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);

    return derivePrepaymentStepState(prepayment, syncPrepaymentEnabled, engagement) === 'failed';
  }

  ngOnInit(): void {
    this.originalDocumentTitle = document.title;
    this.viewEngagement$.subscribe();
    this.showSideDrawerMonitor$.subscribe();

    this._hubspotCleanup = hideHubspot();

    // Store initial view state
    this.store.select(EngagementsState.getSelectedEngagement).pipe(
      tap(selectedEngagement => this._hasViewedEngagement$.next(selectedEngagement?.report?.currentUserHasViewedEngagement ?? false)),
      take(1),
    ).subscribe();

    // Subscribe to document updates after engagement is loaded
    this.engagementLoadedSuccessfully$.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.store.select(EngagementsState.getSelectedEngagement)),
      filter<Engagement>(Boolean),
      take(1),
      tap(engagement => this.subscribeToDocumentUpdates(engagement)),
    ).subscribe();

    // React to active-contact changes from any source (in-sidebar dropdown, secure-link
    // sidebar dropdown, or signature click). The dropdown handlers below just dispatch
    // SetActiveSigningContact; this subscription handles the imperative step-side effects
    // (per-contact signature swap + annotation re-evaluation).
    this.store.select(EngagementsState.getActiveSigningContactId).pipe(
      filter(Boolean),
      distinctUntilChanged(),
      takeUntil(this._unsubscribe$),
    ).subscribe(() => this.applyActiveContactChangeToStep());
  }

  private _previousActiveDocContactId: number | undefined;

  /**
   * Whenever the active signing contact changes, swap the cached signatures for the new
   * contact and re-evaluate annotation visibility on the current step. Tracks the previous
   * documentContactId locally so we can route signatures correctly across contacts.
   */
  private applyActiveContactChangeToStep(): void {
    const stepComponent = this.engagementStepComponents?.first as any;
    if (!stepComponent?.getCurrentUserDocumentContactId) {
      return;
    }

    const newDocContactId: number | undefined = stepComponent.getCurrentUserDocumentContactId();

    if (
      this._previousActiveDocContactId !== undefined
      && newDocContactId !== undefined
      && this._previousActiveDocContactId !== newDocContactId
    ) {
      stepComponent.switchSavedSignatures?.(this._previousActiveDocContactId, newDocContactId);
      stepComponent.reEvaluateAnnotationsForActiveContact?.();
    }

    this._previousActiveDocContactId = newDocContactId;
  }

  ngAfterViewInit(): void {
    this.getCurrentStepTemplateRef$.subscribe();

    // Re-capture the active doc contact ID every time the step component changes (initial
    // mount AND step transitions like letter → consent). _previousActiveDocContactId must
    // be invalidated when the step changes because the docContactId mapping for the same
    // user changes per document; carrying the previous step's docContactId into the next
    // step would cause switchSavedSignatures to be called with a stale source ID and
    // corrupt the cache. Resetting first then calling apply lets the existing logic
    // re-snapshot from the new step component.
    this.engagementStepComponents.changes
      .pipe(
        startWith(this.engagementStepComponents),
        filter(query => !!(query?.first as any)?.getCurrentUserDocumentContactId),
        takeUntil(this._unsubscribe$),
      )
      .subscribe(() => {
        this._previousActiveDocContactId = undefined;
        this.applyActiveContactChangeToStep();
      });
  }

  onSigningContactChanged(contactId: number): void {
    // The active-contact subscription set up in ngOnInit handles the per-contact signature
    // swap and annotation re-evaluation. This handler just dispatches.
    this.store.dispatch(new SetActiveSigningContact(contactId));
  }

  navigateToField(direction: 'previous' | 'next'): void {
    if (this.isProcessingSubmission$.value || this.isStepLoading$.value) {
      return;
    }

    (this.engagementStepComponents.first as unknown as EngagementLetterStepComponent).navigateToField(direction);
  }

  onSubmitStep(isMobile: boolean = false, forceSubmit: boolean = false): void {
    if (this.isProcessingSubmission$.value || this.isStepLoading$.value) {
      return;
    }

    const currentStep = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStep);
    const isOntLastStep = this.store.selectSnapshot(EngagementsState.isSelectedEngagementOnLastStep);
    // PP-3635: the "Update Payment Method" CTA submits an already-complete Payment step in place (swap the
    // method while awaiting signatures), and the "Try Again" CTA retries a hard-failed prepayment on the
    // read-only PAYMENT step — both must skip the complete/readonly navigate shortcut and actually submit
    // (the retry fires through PaymentInfoStepComponent.submit). Otherwise the read-only step would just
    // navigate away and never run the retry.
    const forceSubmitStep = forceSubmit || this.isFailedPrepaymentRetry();
    if (!forceSubmitStep && (currentStep.status === EngagementStepStatus.COMPLETE || currentStep.isReadonly)) {
      if (!isOntLastStep) {
        this.onGoToStep('next');
      } else {
        const lastIncompleteStep = this.store.selectSnapshot(EngagementsState.getSelectedEngagementLastIncompleteStep);
        this.onGoToStep(lastIncompleteStep ? { type: lastIncompleteStep.type, documentId: (lastIncompleteStep.tasks?.find(task => task.documentId))?.documentId } : undefined);
      }
      return;
    }

    this.submitStep(this.getSubmitStepSuccessResponse(), isMobile).subscribe();
  }

  getSubmitStepSuccessResponse(): Observable<any> {
    if (this.isUsingSecureLink()) {
      return of(undefined); // step mgmt is handled in secure-link-container.component.ts
    }

    if (this.store.selectSnapshot(EngagementsState.isSelectedEngagementOnLastIncompleteStep)) {
      return this.completeEngagementSuccessResponse$;
    }

    if (this.store.selectSnapshot(EngagementsState.isSelectedEngagementOnLastStep)) {
      return this.submitLastStepWithIncompletePriorStepsSuccessResponse$;
    }

    return this.submitStepSuccessResponse$;
  }

  onGoToStep(destinationStepType?: DestinationEngagementStepRef): void {
    if (this.isProcessingSubmission$.value || this.isStepLoading$.value) {
      return;
    }

    // Whether the user is selecting the current step or a different one, we should close the sidebar.
    this.toggleSideDrawer$.next(false);

    // In secure link mode, navigate between entities instead of steps
    // The entities are already ordered correctly, so we always navigate to the next/previous entity
    if (this.isUsingSecureLink()) {
      const onNavigateToNextEntity = this.onNavigateToNextEntity();
      const onNavigateToPreviousEntity = this.onNavigateToPreviousEntity();

      if (destinationStepType === 'next' && onNavigateToNextEntity) {
        this.isStepLoading$.next(true);
        onNavigateToNextEntity();
        return;
      }

      if (destinationStepType === 'previous' && onNavigateToPreviousEntity) {
        this.isStepLoading$.next(true);
        onNavigateToPreviousEntity();
        return;
      }

      // For other navigation types in secure link mode, still use the normal flow
      // (e.g., when navigating to a specific step within the same entity)
    }

    const currentStep = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStep);

    // If the destination step is the current step, do nothing
    if (
      typeof destinationStepType !== 'string'
      && destinationStepType?.type === currentStep.type
      && (!destinationStepType?.documentId || currentStep.tasks?.some(task => task.documentId === destinationStepType.documentId))
    ) {
      return;
    }

    const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);

    if (currentStep.type !== EngagementStepType.PAYMENT && selectedEngagement.report.isDocumentViewerOnly && !this._hasViewedEngagement$.value) {
      const selectedEngagementId = this.store.selectSnapshot(EngagementsState.getSelectedEngagementId);
      this.handleDocumentViewerNavigation(selectedEngagementId, typeof destinationStepType === 'string' ? destinationStepType : destinationStepType?.type);
      return;
    }

    this.isStepLoading$.next(true);
    this.store.dispatch(new MoveToEngagementStep(destinationStepType));
  }

  setProcessingSubmission(isProcessingSubmission: boolean): void {
    if (this.isProcessingSubmission$.value === isProcessingSubmission) {
      return;
    }

    this.isProcessingSubmission$.next(isProcessingSubmission);
    this.changeDetectorRef.detectChanges();
  }

  submitStep(successResponse: Observable<any>, isMobile: boolean = false): Observable<any> {
    if (this.isProcessingSubmission$.value || this.isStepLoading$.value) {
      return;
    }

    this.setProcessingSubmission(true);
    // Ignore document updates from the subscription while we're submitting
    this._ignoreDocumentUpdates = true;

    return this.engagementStepComponents.first.submit(isMobile).pipe(
      take(1),
      filter<EngagementStepSubmissionResult>(Boolean),
      switchMap(({ success, errorMessage }) => {
        const currentStepType = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStep)?.type;
        if (success) {
          // Keep controls disabled through the upcoming step transition + next-document load, since
          // finalize() flips isProcessingSubmission$ to false before the recreated step's footer pipeline runs.
          this.isStepLoading$.next(true);

          const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);

          return this.store.dispatch([
            new GetEngagementHub(selectedEngagement.id, true),
            new RemoveEngagementStepPendingChanges(selectedEngagement.id, currentStepType),
          ]).pipe(
            switchMap(_ => successResponse),
          );
        }

        // Letter errors are handled in engagement-letter-step.component
        if (currentStepType == EngagementStepType.LETTER) {
          return of(null);
        }

        this.snackbarService.open(errorMessage || GENERIC_ENGAGEMENT_STEP_SUBMISSION_FAILURE_MESSAGE, SnackbarType.ERROR, { data: { persist: true } });
        return of(null);
      }),
      take(1),
      takeUntil(this._unsubscribe$),
      finalize(() => {
        this.setProcessingSubmission(false);
        // Wait a bit before re-enabling updates to let the subscription catch up
        setTimeout(() => this._ignoreDocumentUpdates = false, 3000);
      }),
    );
  }

  navigateToReassignment(engagementId: number): void {
    this.router.navigate(
      [ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_ENGAGEMENTS_BASE, engagementId],
      { queryParams: { enabledReassignment: true }, queryParamsHandling: 'merge' }
    );
  }

  navigateToEngagementsTable(): void {
    this.router.navigateByUrl(`${ROUTE_TOKEN_MAIN_BASE}/${ROUTE_TOKEN_ENGAGEMENTS_BASE}`);
  }

  marshallCurrentStepTemplateRef(currentStep: EngagementStepType): TemplateRef<EngagementStepComponent> {
    switch (currentStep) {
      case EngagementStepType.PAYMENT:
        return this.paymentInfoStep as unknown as TemplateRef<EngagementStepComponent>;
      case EngagementStepType.ADD_ON:
      case EngagementStepType.CONSENT:
        return this.consentStep;
      default:
        return this.engagementLetterStep;
    }
  }

  marshallFooterButtonTemplateRef(footerButtonType: FooterButtonType): TemplateRef<HTMLElement> {
    switch (footerButtonType) {
      case FooterButtonType.PREVIOUS_FIELD:
        return this.previousFieldFooterButton;
      case FooterButtonType.NEXT_FIELD:
        return this.nextFieldFooterButton;
      case FooterButtonType.PREVIOUS_STEP:
        return this.previousStepFooterButton;
      case FooterButtonType.NEXT_STEP:
        return this.nextStepFooterButton;
      case FooterButtonType.APPROVE_AND_SUBMIT:
        return this.approveAndSubmitFooterButton;
      case FooterButtonType.COMPLETE_ENGAGEMENT:
        return this.completeEngagementFooterButton;
      case FooterButtonType.UPDATE_PAYMENT_METHOD:
        return this.updatePaymentMethodFooterButton;
    }
  }

  ngOnDestroy(): void {
    const engagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    if (engagement?.status === EngagementStatus.EXECUTED) {
      this.store.dispatch(new SetEngagementCurrentStepRef(engagement.id, { type: EngagementStepType.LETTER }))
    }
    this._unsubscribe$.next();
    this._unsubscribe$.complete();

    // Removing this due to a "undefined" title issue
    // document.title = this.originalDocumentTitle;

    this._hubspotCleanup?.();
    this._hubspotCleanup = null;
  }

  /**
   * Only set the toggle state if the user is on mobile
   * @param opened
   */
  drawerStateChanged(opened: boolean): void {
    if (this.breakpointObserver.isMatched([Breakpoints.XSmall, Breakpoints.Small])) {
      this.toggleSideDrawer$.next(opened);
    }
  }

  private handleDocumentViewerNavigation(engagementId: number, destinationStepType: DestinationEngagementStepType): void {
    this.store.dispatch(new MoveToEngagementStep(destinationStepType)).pipe(
      tap(() => this._hasViewedEngagement$.next(true)),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  /**
   * Subscribes to real-time document signature updates via Hasura.
   * When a document is signed by another user, shows a dialog prompting refresh.
   */
  private subscribeToDocumentUpdates(engagement: Engagement): void {
    const documentIds = Object.keys(engagement.documentsInfoById ?? {}).map(Number).filter(id => !isNaN(id));
    if (documentIds.length === 0) {
      return;
    }

    // Get current logged-in user's contact IDs to compare against signers.
    // When multiple identity signing is active, all user contacts should be treated as "current user".
    const allUserContactIds = this.store.selectSnapshot(AppState.getUserContactIds) ?? [];
    const currentUserContactId = this.store.selectSnapshot(AppState.getLoggedInUserContactId);

    this.engagementApiService.subscribeToEngagementDocumentUpdates(documentIds).pipe(
      skip(1), // Skip initial emission (existing signatures when subscription connects)
      filter(docs => docs?.length > 0),
      tap((documentContacts) => {
        // With skip(1), any emission represents a new or changed signature
        // Show modal if any signature is from a different user
        for (const { contactId } of documentContacts) {
          const isCurrentUser = contactId === currentUserContactId || allUserContactIds.includes(contactId);

          // Only show the modal if:
          // 1. The dialog isn't already open
          // 2. The signer is NOT the current user
          // 3. We're not ignoring updates (as a safety fallback)
          if (!this._documentUpdateDialogOpen && !isCurrentUser && !this._ignoreDocumentUpdates) {
            this._documentUpdateDialogOpen = true;

            this.dialog.open(EngagementErrorDialogComponent, {
              data: { concreteStrategy: new EngagementDocumentUpdatedStrategyModel(engagement.id) },
              disableClose: true,
            });
            break; // Only show one dialog
          }
        }
      }),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  private mapProfileToContactSelectOption({ id, contactInfo }: BaseContact): EngagementContactSelectOption {
    return {
      contactId: id,
      name: contactInfo.fullName ?? '',
      email: contactInfo.email ?? '',
      initials: getInitials(contactInfo.fullName),
    };
  }
}
